export const isUpdateAvailable = (current: string, latest: string | null): boolean => {
  if (latest === null) return false
  return current !== latest
}

// 라우트 전환마다 판정이 트리거되므로, 잦은 이동에서 HEAD 요청이 매번 나가지 않도록
// 성공한 조회 결과를 이 시간 동안 재사용한다. 실패(null)는 캐시하지 않아 다음 트리거에서
// 다시 시도한다.
const CACHE_TTL_MS = 60 * 1000

// 강제 업데이트를 수동 검증하는 오버라이드 — 허용된 환경에서 콘솔로
// sessionStorage.setItem('FORCE_UPDATE_DEBUG_LATEST_RELEASE_ID', '아무값') 을 넣으면
// 다음 판정부터 그 값이 최신 releaseId 로 쓰인다. removeItem 으로 해제.
const DEBUG_OVERRIDE_KEY = 'FORCE_UPDATE_DEBUG_LATEST_RELEASE_ID'

type FetcherOptions = {
  /** 디버그 오버라이드 허용 여부 — 앱이 환경(라이브 제외 등)으로 판단해 주입한다 */
  isDebugOverrideAllowed: () => boolean
}

// 최신 releaseId 조회 함수를 만든다 — 배포 파이프라인이 index.html 에 스탬프한
// x-amz-meta-release-id 헤더를 HEAD / 로 읽는다. 실패는 null (fail-open).
export const createLatestReleaseIdFetcher = ({ isDebugOverrideAllowed }: FetcherOptions) => {
  let cached: { releaseId: string; fetchedAt: number } | null = null
  let inFlight: Promise<string | null> | null = null

  const getDebugOverride = (): string | null => {
    if (!isDebugOverrideAllowed()) return null
    try {
      return sessionStorage.getItem(DEBUG_OVERRIDE_KEY)
    } catch {
      return null
    }
  }

  const fetchLatestReleaseId = async (): Promise<string | null> => {
    try {
      const response = await fetch('/', { method: 'HEAD', cache: 'no-store' })
      if (!response.ok) return null
      const releaseId = response.headers.get('x-amz-meta-release-id')
      if (releaseId !== null) {
        cached = { releaseId, fetchedAt: Date.now() }
      }
      return releaseId
    } catch {
      return null
    }
  }

  return async (): Promise<string | null> => {
    const debugOverride = getDebugOverride()
    if (debugOverride !== null) return debugOverride

    if (cached !== null && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      return cached.releaseId
    }
    if (inFlight !== null) return inFlight

    inFlight = fetchLatestReleaseId()
    try {
      return await inFlight
    } finally {
      inFlight = null
    }
  }
}
